import React, { createContext, useContext, useState, ReactNode, SetStateAction, Dispatch, useEffect, useMemo } from "react"
import { MobergTheme } from "../Moberg"
import { MdErrorOutline, MdInfoOutline, MdOutlineWarningAmber } from "react-icons/md"
import { Alert, AlertSource, Severity } from "../Pages/Data/Visualize/DataReview/Types/Alerts"
import { useAlerts } from "../Pages/Data/Visualize/DataReview/Hooks/useAlerts"
import { useRecoilValue } from "recoil"
import { currentPatientFileInfoAtom } from "../Pages/Data/Visualize/DataReview/Atoms/PatientFile"
import { DataSource } from "../Pages/Data/Visualize/DataReview/Types/DataSource"
import { useEndpointProvider } from "./EndpointProvider"
import { useBackendLinksProvider } from "./BackendLinksProvider"
import { useModalProvider } from "./ModalProvider"
import ConfirmationModal from "../Components/ConfirmationModal/ConfirmationModal"
import { useFrontendAlerts } from "../Pages/Data/Visualize/DataReview/Hooks/useFrontendAlerts"

type AlertsContextType = {
    alerts: Alert[]
    selectedAlert: Alert | undefined
    toggleSilenced: (id: number|string) => void
    getAlertTheme: (alert: Alert) => MobergTheme
    getAlertIcon: (alert: Alert) => ReactNode
    setSelectedAlertId: Dispatch<SetStateAction<number| string | undefined>>
}

const AlertsContext = createContext<AlertsContextType | undefined>(undefined)

const severityToTheme = new Map<Severity, MobergTheme>([
    [Severity.LOW, MobergTheme.BLUE],
    [Severity.MEDIUM, MobergTheme.WARNING],
    [Severity.HIGH, MobergTheme.ORANGE],
])


export const AlertsProvider = ({ children }: { children: ReactNode }) => {
    const { LINKS } = useBackendLinksProvider()
    const endpointProvider = useEndpointProvider()
    const { dataSourceMap } = useRecoilValue(currentPatientFileInfoAtom)
    const { alerts, setAlerts } = useAlerts({ dataObjectId: dataSourceMap.get(DataSource.CURRENT_PATIENT) })
    const {frontendAlerts, setFrontendAlerts} = useFrontendAlerts()
    const [selectedAlertId, setSelectedAlertId] = useState<number | string | undefined>(alerts.length > 0 ? alerts[0].id : undefined)
    const { createModal } = useModalProvider()

    const allAlerts = useMemo(() => {   
        return [...frontendAlerts, ...alerts]
    }, [alerts, frontendAlerts])

    const selectedAlert = allAlerts.find(alert => alert.id === selectedAlertId)
    const toggleSilenced = (id: number|string) => {
        const foundAlert = allAlerts.find(alert => alert.id === id)
        if (!foundAlert) {
            console.error("Alert not found", id)
            return
        }

        const desiredSilencedStatus = !foundAlert.silenced

        if (foundAlert.source === AlertSource.BACKEND) {
            // Update on the client (for responsive UI)
            setAlerts(previous => previous.map(alert => (alert.id === id ? { ...alert, silenced: desiredSilencedStatus } : alert)))

            // Update on the server
            const endpoint = structuredClone(LINKS.VISUALIZE.ALERTS)
            endpoint.LINK = `${endpoint.LINK}/${id}`
            endpointProvider.patch(endpoint, { is_silenced: desiredSilencedStatus }).catch(error => {
                console.error(error)

                createModal(
                    <ConfirmationModal 
                        title={"Something went wrong"} 
                        description={"We couldn't update the alert on the server. Check your internet connection, or try to silence the alert again."} 
                        confirmButton={{ 
                            text: "OK", 
                            theme: MobergTheme.RED,
                            onClick: () => {
                                // If the update fails, revert the silenced state of the alert on the client
                                setAlerts(previous => previous.map(alert => (alert.id === id ? { ...alert, silenced: !desiredSilencedStatus } : alert)))
                            }
                        }}
                        showCancelButton={false}
                    />
                )
            })
        } 
        else{
            // Update on the client (for responsive UI)
            setFrontendAlerts(previous => previous.map(alert => (alert.id === id ? { ...alert, silenced: desiredSilencedStatus } : alert)))

            // Save on local storage
            const stored = localStorage.getItem("silencedFrontendAlerts")
            const silencedIds = new Set(stored ? JSON.parse(stored) : [])

            if(desiredSilencedStatus){
                silencedIds.add(id)
            }
            else{
                silencedIds.delete(id)
            }

            localStorage.setItem("silencedFrontendAlerts", JSON.stringify(Array.from(silencedIds)))
        }
    }

    const getAlertTheme = (alert: Alert) => severityToTheme.get(alert.severity) ?? MobergTheme.BLUE
    const getAlertIcon = (alert: Alert) => {
        const iconSize = 81
        switch (alert.severity) {
            case Severity.LOW: return <MdInfoOutline size={iconSize} />
            case Severity.MEDIUM: return <MdErrorOutline size={iconSize} />
            case Severity.HIGH: return <MdOutlineWarningAmber size={iconSize} />
            default:
                return <MdInfoOutline size={iconSize} />
        }
    }

    // Select an alert if there is no alert selected.
    useEffect(() => {
        if (!selectedAlert && allAlerts.length > 0) {
            setSelectedAlertId(allAlerts[0].id)
        }

    }, [allAlerts, selectedAlert])

    return (
        <AlertsContext.Provider value={{ alerts: allAlerts, selectedAlert, toggleSilenced, getAlertTheme, getAlertIcon, setSelectedAlertId }}>
            {children}
        </AlertsContext.Provider>
    )
}

export const useAlertsProvider = () => {
    const context = useContext(AlertsContext)
    
    if (!context) {
        throw new Error("useAlerts must be used within an AlertsProvider")
    }
    
    return context
}
